import { Action, Selector, StateContext, State, Store } from '@ngxs/store';
import {
  AddPerson,
  AddPersonOnServer,
  AddPersonAddress,
  AddPersonProfission,
  AddPersonBank,
  AddPersonSuitability,
  AddPersonAuthorizations,
  AddPersonFiles,
  AddPersonRegistrationHistory,
  InitPersonFromServer,
  PersonConfirmRegistration,
  RegisterInvitationCode,
  AddPersonSuitabilityScore,
  UpdatePerson,
  AddPersonSuitabilityQuestions,
  UpdatePersonSuitabilityQuestionsOnServer,
  UpdatePersonPpe,
  UpdatePersonAuthorizationsOnServer,
  UpdatePersonBankOnServer,
  UpdatePersonContractOnServer,
  UpdatePersonProfissionOnServer,
  UpdatePersonAddressOnServer,
  UpdatePersonFilesOnServer,
  UpdatePersonSuitabilityScoreOnServer,
  RemovePersonFiles,
  RemovePersonFilesOnServer,
  DisablePersonFile,
  DisablePersonFileOnServer,
  UpdatePersonSuitabilityOnServer,
  UpdatePersonPpeOnServer,
  CleanPerson,
  UpdatePersonFinancialStatement,
  UpdatePersonFinancialStatementOnServer,
  AddOnlyPersonOnServer,
  AddPersonOnlyState,
  AddPersonPix,
  UpdatePersonPixOnServer,
  AddAgent,
  AddPersonSuitabilityFile,
  AddPersonContract,
  AddPersonBeneficiaries,
  UpdatePersonBeneficiariesOnServer
} from '../actions/person-register.action';
import { PersonData } from '../../models/agents/person-data';
import { RegistrationService } from '../../services/registration.service';
import { AgentRegister } from '../../models/agents/agent-register';
import { RegistrationHistory } from '../../models/agents/registration-history';
import { RegistratinEntityType } from '../../models/enums/registration-entity-type.enum';
import { RegistrationControlItem } from '../../models/enums/registration-control-item.enum';
import { PersonService } from '../../services/person.service';
import { Suitability } from '../../models/suitability/suitability';
import { Observable } from 'rxjs/Observable';
import { Invitation } from '../../models/agents/invitation.ts';
import { PersonType } from '../../models/enums/person-type.enum';
import { AddPartners } from '../actions/company-register.action';
import { Files } from '../../models/agents/files';
import { tap } from 'rxjs/operators';

export class PersonStateModel {
  serverIdentify: string | null;
  invitationIdentify: string | null;
  invitation: Invitation | null;
  document: string | null;
  person: PersonData | null;
  registrationHistory: RegistrationHistory[] | null;
  agent: Partial<AgentRegister>;
}

@State<PersonStateModel>({
  name: 'person',
  defaults: {
    person: null,
    serverIdentify: null,
    registrationHistory: null,
    invitationIdentify: null,
    document: null,
    invitation: null,
    agent: null
  }
})
export class PersonState {
  constructor(
    protected personService: PersonService,
    protected registrationService: RegistrationService,
    protected store: Store
  ) {}

  @Selector()
  static invitation(state: PersonStateModel) {
    return state.invitation;
  }

  @Selector()
  static person(state: PersonStateModel) {
    return state.person;
  }

  @Selector()
  static serverIdentify(state: PersonStateModel) {
    return state.serverIdentify;
  }

  @Selector()
  static agent(state: PersonStateModel) {
    return state.agent;
  }

  @Selector()
  static registrationHistory(state: PersonStateModel) {
    return state.registrationHistory;
  }

  @Action(AddAgent)
  addAgent(
    { patchState, getState }: StateContext<PersonStateModel>,
    { payload }: AddAgent
  ) {
    patchState({ agent: payload });
  }

  @Action(RegisterInvitationCode)
  registerInvitationCode(
    { getState, patchState }: StateContext<PersonStateModel>,
    { invitationIdentify }: RegisterInvitationCode
  ) {
    const currentState = getState();
    patchState({ ...currentState, invitationIdentify });
  }

  @Action(PersonConfirmRegistration)
  personConfirmRegistration(
    { getState, patchState }: StateContext<PersonStateModel>,
    { confirmed }: PersonConfirmRegistration
  ) {
    const currentState = getState();
    const agentId = getState().serverIdentify;

    currentState.person.confirmed = confirmed;
    patchState({ ...currentState });

    return this.personService.confirmRegistration(agentId);
  }

  @Action(AddPerson)
  addPerson(
    { patchState }: StateContext<PersonStateModel>,
    { person }: AddPerson
  ) {
    patchState({ person });
    return this.store.dispatch(new AddPersonOnServer(person));
  }

  @Action(UpdatePerson)
  updatePerson(
    { getState, patchState }: StateContext<PersonStateModel>,
    { person }: UpdatePerson
  ) {
    const currentPerson = getState().person;
    const agentId = getState().serverIdentify;

    person.address = currentPerson.address;
    person.authorizations = currentPerson.authorizations;
    person.bankData = currentPerson.bankData;
    person.files = currentPerson.files;
    person.inviteID = currentPerson.inviteID;
    person.profession = currentPerson.profession;
    person.suitability = currentPerson.suitability;
    person.financialStatement = currentPerson.financialStatement;
    person.pixs = currentPerson.pixs;
    person.agentId = agentId;
    person.contractData = currentPerson.contractData;
    person.beneficiaries = currentPerson.beneficiaries;

    patchState({ person });

    return this.personService.updatePerson(agentId, person);
  }

  @Action(AddPersonFiles)
  updatePersonFiles(
    { getState, patchState }: StateContext<PersonStateModel>,
    { files }: AddPersonFiles
  ) {
    const person = getState().person;
    let allFiles = person.files ? [...person.files, ...files] : files;
    allFiles = allFiles.filter(
      (v, i, a) => a.findIndex((t) => t.fileName === v.fileName) === i
    );
    patchState({ person: { ...person, files: allFiles } });

    return this.store.dispatch(new UpdatePersonFilesOnServer(allFiles));
  }

  @Action(DisablePersonFile)
  disablePersonFile(
    { getState, patchState }: StateContext<PersonStateModel>,
    { file }: DisablePersonFile
  ) {
    const person = getState().person;

    if (!person) {
      return;
    }

    const personFiles = person.files.map((personFile) => {
      if (personFile.fileName === file.fileName) {
        personFile.isDeleted = true;
      }

      return personFile;
    });

    if (!person.files || person.files.length) {
      person.files = new Array<Files>();
    }

    person.files = personFiles;
    patchState({ person });
    return this.store.dispatch(new DisablePersonFileOnServer(file.fileUrl));
  }

  @Action(DisablePersonFileOnServer)
  disablePersonFileOnServer(
    { getState, patchState }: StateContext<PersonStateModel>,
    { fileUrl }: DisablePersonFileOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.disablePersonFile(agentId, fileUrl);
  }

  @Action(RemovePersonFiles)
  removePersonFiles(
    { getState, patchState }: StateContext<PersonStateModel>,
    { index }: RemovePersonFiles
  ) {
    const person = getState().person;
    const blockReference = person.files[index].fileUrl;
    person.files.splice(index, 1);
    patchState({ person });

    return this.store.dispatch(new RemovePersonFilesOnServer(blockReference));
  }

  @Action(RemovePersonFilesOnServer)
  removePersonFilesOnServer(
    { getState, patchState }: StateContext<PersonStateModel>,
    { blockReference }: RemovePersonFilesOnServer
  ) {
    return this.personService.removePersonFiles(blockReference);
  }

  @Action(AddPersonAddress)
  addPersonAddress(
    { getState, patchState }: StateContext<PersonStateModel>,
    { address }: AddPersonAddress
  ) {
    const person = getState().person;
    person.address = address;
    patchState({ person });
    return this.store.dispatch(new UpdatePersonAddressOnServer(address));
  }

  @Action(AddPersonProfission)
  addPersonProfission(
    { getState, patchState }: StateContext<PersonStateModel>,
    { profission }: AddPersonProfission
  ) {
    const person = getState().person;
    person.profession = profission;
    patchState({ person });
    return this.store.dispatch(new UpdatePersonProfissionOnServer(profission));
  }

  @Action(AddPersonBank)
  addPersonBank(
    { getState, patchState }: StateContext<PersonStateModel>,
    { bankData }: AddPersonBank
  ) {
    const person = getState().person;
    person.bankData = bankData;
    patchState({ person });
    return this.store.dispatch(new UpdatePersonBankOnServer(bankData));
  }

  @Action(AddPersonContract)
  addPersonContract(
    { getState, patchState }: StateContext<PersonStateModel>,
    { contractData }: AddPersonContract
  ) {
    const person = getState().person;
    person.contractData = contractData;
    patchState({ person });
    return this.store.dispatch(new UpdatePersonContractOnServer(contractData));
  }

  @Action(AddPersonBeneficiaries)
  addPersonBeneficiaries(
    { getState, patchState }: StateContext<PersonStateModel>,
    { beneficiaries }: AddPersonBeneficiaries
  ) {
    const person = getState().person;
    person.beneficiaries = beneficiaries;
    patchState({ person });
    return this.store.dispatch(
      new UpdatePersonBeneficiariesOnServer(beneficiaries)
    );
  }

  @Action(AddPersonPix)
  addPersonPix(
    { getState, patchState }: StateContext<PersonStateModel>,
    { pixs }: AddPersonPix
  ) {
    const person = getState().person;
    person.pixs = pixs;
    patchState({ person });
    return this.store.dispatch(new UpdatePersonPixOnServer(pixs));
  }

  @Action(AddPersonAuthorizations)
  addPersonAuthorizations(
    { getState, patchState }: StateContext<PersonStateModel>,
    { authorizations }: AddPersonAuthorizations
  ) {
    const person = getState().person;
    person.authorizations = authorizations;
    patchState({ person });
    return this.store.dispatch(
      new UpdatePersonAuthorizationsOnServer(authorizations)
    );
  }

  @Action(AddPersonSuitability)
  addPersonSuitability(
    { getState, patchState }: StateContext<PersonStateModel>,
    { suitability }: AddPersonSuitability
  ) {
    const person = getState().person;
    person.suitability = suitability;
    patchState({ person });
    return this.store.dispatch(
      new UpdatePersonSuitabilityOnServer(suitability)
    );
  }

  @Action(UpdatePersonPpe)
  updatePersonPpe(
    { getState, patchState }: StateContext<PersonStateModel>,
    { ppe }: UpdatePersonPpe
  ) {
    const person = getState().person;
    person.authorizations = {
      ...person.authorizations,
      ppe: ppe.ppe,
      politicalExposedInformation: ppe.politicalExposedInformation
    };
    patchState({
      person
    });
    return this.store.dispatch(new UpdatePersonPpeOnServer(ppe));
  }

  @Action(AddPersonSuitabilityQuestions)
  addPersonSuitabilityQuestions(
    { patchState, getState }: StateContext<PersonStateModel>,
    { suitability }: AddPersonSuitabilityQuestions
  ) {
    const person = getState().person;

    if (!person.suitability) {
      person.suitability = new Suitability();
    }

    person.suitability = suitability;
    patchState({ person });
    return this.store.dispatch(
      new UpdatePersonSuitabilityQuestionsOnServer(suitability)
    );
  }

  @Action(AddPersonSuitabilityScore)
  addPersonSuitabilityScore(
    { patchState, getState }: StateContext<PersonStateModel>,
    { suitabilityScore }: AddPersonSuitabilityScore
  ) {
    const person = getState().person;

    if (!person.suitability) {
      person.suitability = new Suitability();
    }

    person.suitability.profile = suitabilityScore.profile;
    person.suitability.score = suitabilityScore.score;

    patchState({ person });
    return this.store.dispatch(
      new UpdatePersonSuitabilityScoreOnServer(suitabilityScore)
    );
  }

  @Action(UpdatePersonFilesOnServer)
  updatePersonFilesOnServer(
    { getState }: StateContext<PersonStateModel>,
    { files }: UpdatePersonFilesOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateFiles(agentId, files);
  }

  @Action(UpdatePersonAddressOnServer)
  updatePersonAddressOnServer(
    { getState }: StateContext<PersonStateModel>,
    { address }: UpdatePersonAddressOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateAddress(agentId, address);
  }

  @Action(UpdatePersonProfissionOnServer)
  updatePersonProfissionOnServer(
    { getState }: StateContext<PersonStateModel>,
    { profission }: UpdatePersonProfissionOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateProfission(agentId, profission);
  }

  @Action(UpdatePersonBankOnServer)
  updatePersonBankOnServer(
    { getState }: StateContext<PersonStateModel>,
    { bankData }: UpdatePersonBankOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateBankData(agentId, bankData);
  }

  @Action(UpdatePersonContractOnServer)
  updatePersonContractOnServer(
    { getState }: StateContext<PersonStateModel>,
    { contractData }: UpdatePersonContractOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateContractData(agentId, contractData);
  }

  @Action(UpdatePersonBeneficiariesOnServer)
  updatePersonBeneficiariesOnServer(
    { getState }: StateContext<PersonStateModel>,
    { beneficiariesData }: UpdatePersonBeneficiariesOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateBeneficiariesData(
      agentId,
      beneficiariesData
    );
  }

  @Action(UpdatePersonPixOnServer)
  updatePersonPixOnServer(
    { getState }: StateContext<PersonStateModel>,
    { pixs }: UpdatePersonPixOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updatePixData(agentId, pixs);
  }

  @Action(UpdatePersonAuthorizationsOnServer)
  updatePersonAuthorizationsOnServer(
    { getState }: StateContext<PersonStateModel>,
    { authorizations }: UpdatePersonAuthorizationsOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateAuthorizations(agentId, authorizations);
  }

  @Action(UpdatePersonSuitabilityOnServer)
  updatePersonSuitabilityOnServer(
    { getState }: StateContext<PersonStateModel>,
    { suitability }: UpdatePersonSuitabilityOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateSuitability(agentId, suitability);
  }

  @Action(UpdatePersonPpeOnServer)
  updatePersonPpeOnServer(
    { getState }: StateContext<PersonStateModel>,
    { ppe }: UpdatePersonPpe
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updatePpe(agentId, ppe);
  }

  @Action(UpdatePersonSuitabilityQuestionsOnServer)
  updatePersonSuitabilityQuestionsOnServer(
    { getState }: StateContext<PersonStateModel>,
    { suitability }: UpdatePersonSuitabilityQuestionsOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateSuitabilityQuestions(agentId, suitability);
  }

  @Action(UpdatePersonSuitabilityScoreOnServer)
  updatePersonSuitabilityScoreOnServer(
    { getState }: StateContext<PersonStateModel>,
    { suitabilityScore }: UpdatePersonSuitabilityScoreOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService.updateSuitabilityScore(agentId, suitabilityScore);
  }

  @Action(AddPersonOnServer)
  addPersonOnServer(
    { patchState }: StateContext<PersonStateModel>,
    { person, personType }: AddPerson
  ) {
    const companyObservable = new Observable((observer) => {
      if (!person.agentId) {
        const agentRegister = this.createAgentPayload(
          person,
          personType,
          person.agentId
        );
        this.registrationService
          .registerAgent(agentRegister)
          .subscribe((res) => {
            patchState({
              serverIdentify: res.agent.id,
              registrationHistory: res.agent.registrationHistory
            });

            this.personService
              .registerPerson({ agentId: res.agent.id, person })
              .subscribe(
                (person) => {
                  observer.next(person);
                  observer.complete();
                },
                (error) => {
                  observer.error(error);
                }
              );
          });
      } else {
        this.personService
          .registerPerson({ agentId: person.agentId, person })
          .subscribe(
            (person) => {
              observer.next(person);
              observer.complete();
            },
            (error) => {
              observer.error(error);
            }
          );
      }
    });
    return companyObservable;
  }

  @Action(AddPersonOnlyState)
  addPersonOnlyState(
    { patchState, getState }: StateContext<PersonStateModel>,
    { person, agentId }: AddPersonOnlyState
  ) {
    patchState({ person, serverIdentify: agentId });
  }

  @Action(AddOnlyPersonOnServer)
  addOnlyPersonOnServer(
    { patchState }: StateContext<PersonStateModel>,
    { person, agentId }: AddOnlyPersonOnServer
  ) {
    patchState({ person });
    const personObservable = new Observable((observer) => {
      this.personService.registerPerson({ agentId: agentId, person }).subscribe(
        (person) => {
          observer.next(person);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        }
      );
    });
    return personObservable;
  }

  @Action(UpdatePersonFinancialStatement)
  updatePersonFinancialStatement(
    { patchState, getState }: StateContext<PersonStateModel>,
    { financialAssets }: UpdatePersonFinancialStatement
  ) {
    const person = getState().person;

    person.financialStatement = financialAssets;
    patchState({ person });
  }

  @Action(UpdatePersonFinancialStatementOnServer)
  updatePersonFinancialStatementOnServer(
    { getState, patchState }: StateContext<PersonStateModel>,
    { financialAssets }: UpdatePersonFinancialStatementOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.personService
      .updateFinancialStatement(agentId, financialAssets)
      .pipe(
        tap((response: PersonStateModel) => {
          patchState({ person: response.person });
        })
      );
  }

  @Action(CleanPerson)
  cleanCompany(
    { patchState }: StateContext<PersonStateModel>,
    { payload }: CleanPerson
  ) {
    patchState({ person: payload });
  }

  @Action(InitPersonFromServer)
  initFromServer(
    { patchState }: StateContext<PersonStateModel>,
    {
      person,
      registrationHistory,
      serverIdentify,
      invitation
    }: InitPersonFromServer
  ) {
    patchState({ person, serverIdentify, registrationHistory, invitation });
  }

  @Action(AddPersonRegistrationHistory)
  addPersonRegistrationHistory(
    { setState, getState }: StateContext<PersonStateModel>,
    { history }: AddPersonRegistrationHistory
  ) {
    const state = getState();
    if (
      state.registrationHistory.findIndex(
        (args) => args.currentStep === history.currentStep
      ) === -1
    ) {
      state.registrationHistory.push(history);
      setState({ ...state });
      return this.registrationService
        .insertNewTrack({
          agentId: state.serverIdentify,
          registrationHistory: history
        })
        .subscribe();
    }
  }

  @Action(AddPersonSuitabilityFile)
  addPersonSuitabilityFile(
    { patchState, getState }: StateContext<PersonStateModel>,
    { virtualRawFingerprint }: AddPersonSuitabilityFile
  ) {
    const person = getState().person;
    const agentId = getState().serverIdentify;
    return this.personService
      .generateSuitabilityFile(agentId, virtualRawFingerprint)
      .pipe(
        tap((files: Array<Files>) => {
          person.files = files;
          patchState({ person });
        })
      );
  }

  createAgentPayload(person: PersonData, type: PersonType, agentId = null): AgentRegister {
    return new AgentRegister({
      id: agentId,
      document: person.socialNumber,
      inviteID: person.inviteID,
      personType: type,
      entitiesType: [RegistratinEntityType.Cotista],
      status: 'Em preenchimento',
      registrationHistory: this.createInitalRegistrationHistory(),
      pep: false,
      cft: false,
      cftVerificationDate: '',
      investorRepresentative: null,
      email: null,
      cellPhone: null,
      HandShake: null,
      isExternal: null,
      isRepresentativeFilling: null,
      madeByRepresentative: null,
      updateProcess: false,
      requestedLastUpdateAt: null,
      distributor: person.distributor
    });
  }

  createInitalRegistrationHistory(): RegistrationHistory[] {
    return [
      this.registrationService.getRegistrationItem(
        RegistrationControlItem.InvestorData
      ),
      this.registrationService.getRegistrationItem(
        RegistrationControlItem.Address
      )
    ];
  }
}
